import { useEffect, useState } from 'react'
import { COLLECTIONS } from '../../shared/schema'
import { fetchStatus, refreshAll, resetDemo, runNotionSetup } from '../lib/api'
import { DEMO } from '../lib/config'
import { resetPartnerPassword, updatePassword, useSession } from '../lib/auth'
import { Badge, Button, PageHeader, useToast } from '../components/ui'

export function Ajustes() {
  const { session, name } = useSession()
  const [status, setStatus] = useState<any>(null)
  const [error, setError] = useState('')
  const [log, setLog] = useState<string[]>([])
  const [running, setRunning] = useState(false)
  const [setupError, setSetupError] = useState('')
  const load = () => fetchStatus().then(setStatus).catch(e => setError(e.message))
  useEffect(() => { load() }, [])
  const faltan = status?.bases?.filter((b: any) => !b.ok).length ?? 0

  async function crearBases() {
    setRunning(true); setLog([]); setSetupError('')
    try {
      await runNotionSetup(msg => setLog(l => [...l, msg]))
      await load()
      refreshAll()
    } catch (e: any) { setSetupError(e.message) } finally { setRunning(false) }
  }
  const Row = ({ label, ok, note }: { label: string; ok: boolean | undefined; note: string }) => (
    <tr><td><strong>{label}</strong><small className="block muted">{note}</small></td><td>{ok == null ? <Badge tone="muted">—</Badge> : ok ? <Badge tone="olive">Conectado</Badge> : <Badge tone="terra">Falta configurar</Badge>}</td></tr>
  )
  return (
    <div className="page narrow">
      <PageHeader eyebrow="Ajustes" title="Conexión y cuenta" subtitle={`Sesión iniciada como ${name} (${session?.user.usuario}).`} />
      {DEMO ? (
        <section className="panel pad">
          <h3>Vista previa</h3>
          <p>Esta versión funciona con datos de ejemplo guardados solo en este navegador. Al publicarla en Cloudflare con las variables de entorno, cada módulo lee y escribe en las bases de Notion de REVELA.</p>
          <Button variant="ghost" icon="refresh" onClick={() => resetDemo()}>Restaurar datos de ejemplo</Button>
        </section>
      ) : (
        <>
        <section className="panel pad setup-box">
          <div className="setup-head">
            <div>
              <h3>Bases de datos en Notion</h3>
              <p className="muted">
                {status?.paginaDatos && !status.paginaDatos.ok ? status.paginaDatos.error
                  : faltan ? `Faltan ${faltan} de 8 bases. Este botón las crea dentro de la página "REVELA Internal · Datos".`
                  : status ? 'Las 8 bases están creadas. Podés volver a tocar el botón cuando se agreguen campos nuevos: completa lo que falte sin borrar nada.'
                  : 'Revisando la conexión con Notion…'}
              </p>
            </div>
            <Button variant={faltan ? 'accent' : 'ghost'} icon="layers" busy={running} disabled={!status?.paginaDatos?.ok} onClick={crearBases}>
              {faltan ? 'Crear bases en Notion' : 'Revisar y completar bases'}
            </Button>
          </div>
          {log.length > 0 && <ul className="setup-log">{log.map((l, i) => <li key={i}>{l}</li>)}</ul>}
          {setupError && <p className="form-error">{setupError}</p>}
        </section>
        <section className="panel">
          {error && <p className="form-error pad">{error}</p>}
          <div className="table-wrap">
            <table className="table">
              <tbody>
                <Row label="Notion" ok={status?.notion} note="NOTION_TOKEN de la integración interna." />
                <Row label="Página REVELA Internal · Datos" ok={status?.paginaDatos?.ok} note="NOTION_PARENT_PAGE_ID y la página conectada a la integración." />
                {(status?.bases ?? Object.values(COLLECTIONS).map(c => ({ db: c.db, env: c.env, ok: undefined }))).map((b: any) => <Row key={b.db} label={b.db} ok={b.ok} note="Base de Notion" />)}
                <Row label="Contraseñas, fotos y documentos" ok={status?.archivos} note="Espacio KV de Cloudflare enlazado como REVELA." />
                <Row label="Google Calendar" ok={status?.googleCalendar} note="Opcional: GOOGLE_SERVICE_ACCOUNT_JSON y GOOGLE_CALENDAR_ID." />
              </tbody>
            </table>
          </div>
        </section>
        </>
      )}
      <section className="panel pad">
        <h3>Cómo se sincroniza con Notion</h3>
        <ul className="recs">
          <li>Cada vez que guardás, la app espera la confirmación de Notion antes de mostrar “Guardado”.</li>
          <li>Los cambios hechos directamente en Notion aparecen en la app en menos de un minuto, al volver a la pestaña o con el botón de actualizar de arriba.</li>
          <li>Si las dos editan el mismo registro a la vez, la app avisa antes de sobrescribir.</li>
          <li>Eliminar algo desde la app lo manda a la papelera de Notion, desde donde se puede recuperar.</li>
        </ul>
      </section>
      {!DEMO && <Cuenta socias={status?.socias ?? []} yo={session?.user.usuario} />}
    </div>
  )
}

function Cuenta({ socias, yo }: { socias: { usuario: string; nombre: string }[]; yo?: string }) {
  const toast = useToast()
  const [p1, setP1] = useState('')
  const [p2, setP2] = useState('')
  const [busy, setBusy] = useState(false)
  const [armed, setArmed] = useState('')
  const otras = socias.filter(s => s.usuario !== yo)
  return (
    <section className="panel pad">
      <div className="account-grid">
        <form onSubmit={async e => {
          e.preventDefault()
          if (p1.length < 8) return toast('Usá al menos 8 caracteres.', 'error')
          if (p1 !== p2) return toast('Las contraseñas no coinciden.', 'error')
          setBusy(true)
          try { await updatePassword(p1); setP1(''); setP2(''); toast('Contraseña actualizada') } catch (err: any) { toast(err.message, 'error') } finally { setBusy(false) }
        }}>
          <h3>Cambiar mi contraseña</h3>
          <div className="field"><label htmlFor="cp1">Nueva contraseña</label><input id="cp1" type="password" autoComplete="new-password" value={p1} onChange={e => setP1(e.target.value)} /></div>
          <div className="field"><label htmlFor="cp2">Repetila</label><input id="cp2" type="password" autoComplete="new-password" value={p2} onChange={e => setP2(e.target.value)} /></div>
          <div><Button variant="primary" type="submit" busy={busy} icon="check">Guardar</Button></div>
        </form>
        <div className="col gap-s">
          <h3>Si tu socia olvidó su contraseña</h3>
          <p className="muted small">Restablecerla le permite volver a entrar con la contraseña inicial de REVELA. Al entrar, la app le pide elegir una nueva.</p>
          {otras.map(s => armed === s.usuario
            ? <Button key={s.usuario} variant="accent" size="sm" onClick={async () => {
                try { await resetPartnerPassword(s.usuario); toast(`Listo: ${s.nombre} ya puede entrar con la contraseña inicial`) } catch (err: any) { toast(err.message, 'error') } finally { setArmed('') }
              }}>Confirmar: restablecer la de {s.nombre}</Button>
            : <Button key={s.usuario} variant="ghost" size="sm" icon="refresh" onClick={() => setArmed(s.usuario)}>Restablecer contraseña de {s.nombre}</Button>)}
          {!otras.length && <p className="muted small">Cuando agregues a tu socia en SOCIAS, aparece acá.</p>}
        </div>
      </div>
    </section>
  )
}
